/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn/ResourceItem
 * @desc Lists for ResourceItem
 */

import { EntityTemplateSectionItem } from 'labstep-web/components/Entity/Template/SectionItem';
import { MetadataActionCreateModalDropdown } from 'labstep-web/components/Metadata/Action/Create/Modal/Dropdown';
import { ICONS } from 'labstep-web/constants/icons';
import { SectionHeader } from 'labstep-web/core/Header';
import { EntityDataGridToolPanelListItem } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/ListItem';
import type { IndexTemplatableEntity } from 'labstep-web/models';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import { Metadata } from 'labstep-web/models/metadata';
import {
  type MetadataParent,
  MetadataType,
} from 'labstep-web/models/metadata/types';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';

export const getResourceItemLists = (
  entityView: EntityView,
  selectMetadata: (
    metadata: Metadata,
    parentEntityClass: string,
  ) => void,
  colIds: string[],
  template?: IndexTemplatableEntity,
) => {
  const resourceMetadatas = {
    key: Metadata.entityName,
    entityName: Metadata.entityName,
    searchField: 'label_like',
    params: template
      ? {
          metadata_thread_id: template.metadata_thread.id,
          count: 8,
        }
      : {
          permission_class: Resource.entityName,
          group_id: entityView.group.id,
          group_by_label: 1,
          sort: 'label',
          count: 8,
        },
    header: (
      <SectionHeader label="Resource Metadata Fields">
        {template && (
          <EntityTemplateSectionItem template={template} />
        )}
      </SectionHeader>
    ),
    listItem: (entity: object) => {
      const metadata = entity as Metadata;

      return (
        <EntityDataGridToolPanelListItem
          icon={
            ICONS.metadata.type[
              metadata.type as keyof typeof ICONS.metadata.type
            ] || 'database'
          }
          actionIcon={
            metadata.type === MetadataType.molecule
              ? 'arrow right'
              : 'eye'
          }
          text={metadata.label || 'Untitled data field'}
          onClick={() =>
            selectMetadata(metadata, Resource.entityName)
          }
          disabled={colIds.includes(metadata.colId)}
        />
      );
    },
    bottomAction: template ? (
      <MetadataActionCreateModalDropdown
        isTemplate={true}
        entity={template as MetadataParent}
      />
    ) : undefined,
  };
  const resourceItemsMetadatas = {
    key: Metadata.entityName,
    entityName: Metadata.entityName,
    searchField: 'label_like',
    params: (template as Resource)?.resource_item_template
      ? {
          metadata_thread_id: (template as Resource)
            .resource_item_template?.metadata_thread.id,
          count: 8,
        }
      : {
          permission_class: ResourceItem.entityName,
          group_id: entityView.group.id,
          group_by_label: 1,
          sort: 'label',
          count: 8,
        },
    header: (
      <SectionHeader label="Item Metadata Fields">
        {template && (
          <EntityTemplateSectionItem template={template} />
        )}
      </SectionHeader>
    ),
    listItem: (entity: object) => (
      <EntityDataGridToolPanelListItem
        icon={
          ICONS.metadata.type[
            (entity as Metadata)
              .type as keyof typeof ICONS.metadata.type
          ] || 'database'
        }
        actionIcon={
          (entity as Metadata).type === MetadataType.molecule
            ? 'arrow right'
            : 'eye'
        }
        text={(entity as Metadata).label || 'Untitled data field'}
        onClick={() =>
          selectMetadata(entity as Metadata, Resource.entityName)
        }
        disabled={colIds.includes((entity as Metadata).colId)}
      />
    ),
    bottomAction: (template as Resource)?.resource_item_template ? (
      <MetadataActionCreateModalDropdown
        isTemplate={true}
        entity={
          (template as Resource)
            .resource_item_template as MetadataParent
        }
      />
    ) : undefined,
  };

  if (entityView.context === 'resource_show') {
    return [resourceItemsMetadatas];
  }

  return [resourceMetadatas, resourceItemsMetadatas];
};
